import React, { useState, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, User, UserPlus, LogIn, Phone, Mail, Lock, Eye, EyeOff,
  Shield, Heart, Loader2, ArrowRight, ArrowLeft, Smartphone, CheckCircle2,
  Sparkles, Activity, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { usePatientAuth } from '@/contexts/PatientAuthContext';
import { useToast } from '@/hooks/use-toast';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { isValidEmail, isValidMobile } from '@/lib/validators';

interface PatientAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  redirectUrl?: string;
}

type AuthStep = 'choose' | 'guest_register' | 'mobile_login' | 'patient_register';

const FloatingParticle = ({ delay, x, y }: { delay: number; x: string; y: string }) => (
  <motion.div
    className="absolute w-1.5 h-1.5 rounded-full bg-white/30"
    style={{ left: x, top: y }}
    animate={{ y: [0, -15, 0], opacity: [0.3, 0.8, 0.3], scale: [1, 1.3, 1] }}
    transition={{ duration: 3, delay, repeat: Infinity, ease: 'easeInOut' }}
  />
);

const PulseRing = () => (
  <motion.div
    className="absolute inset-0 rounded-2xl border-2 border-white/20"
    animate={{ scale: [1, 1.15], opacity: [0.5, 0] }}
    transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
  />
);

const PatientAuthModal = forwardRef<HTMLDivElement, PatientAuthModalProps>(({
  isOpen,
  onClose,
  onSuccess,
// eslint-disable-next-line @typescript-eslint/no-unused-vars
}, _ref) => {
  const { signIn, signUp, mobileLogin, visitorRegister } = usePatientAuth();
  const { toast } = useToast();
  const { loginEnabled, signupEnabled } = useAuthConfig('patient');
  const [step, setStep] = useState<AuthStep>('choose');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState<'email' | 'otp'>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');

  const [guestData, setGuestData] = useState({ name: '', mobile: '', email: '' });
  const [mobileLoginNum, setMobileLoginNum] = useState('');
  const [registerData, setRegisterData] = useState({
    name: '', email: '', mobile: '', password: '', confirmPassword: ''
  });
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  // Every successful login/register already triggers the real browser permission
  // popup in the background (PatientAuthContext.saveSession → subscribeToPush) — this
  // just gives it a visible reason on-screen instead of a popup appearing out of
  // nowhere while the modal is closing. Shown every time, since a mobile-only
  // visitor's session expires and they pass through this modal on nearly every visit.
  const [showNotifStep, setShowNotifStep] = useState(false);

  const resetAndClose = () => {
    setStep('choose');
    setLoading(false);
    setShowNotifStep(false);
    onClose();
  };

  const finishWithNotifStep = () => {
    setShowNotifStep(true);
    setTimeout(resetAndClose, 1500);
  };

  const handleForgotPasswordRequest = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    const email = forgotEmail.trim();
    if (!email || !isValidEmail(email)) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid registered email address', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { bridgeApi } = await import('@/lib/bridgeApi');
      const res = await bridgeApi.post<any>('patient_forgot_password', { email });
      toast({ title: 'Code Sent', description: res.message || 'If this email is registered, a verification code has been sent.' });
      setForgotStep('otp');
    } catch (error: any) {
      toast({ title: 'Connection Error', description: error.message || 'Unable to process request. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (forgotOtp.length !== 6) {
      toast({ title: 'Invalid Code', description: 'Please enter the 6-digit verification code', variant: 'destructive' });
      return;
    }
    if (forgotNewPassword.length < 6) {
      toast({ title: 'Invalid Password', description: 'New password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      toast({ title: 'Password Mismatch', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { bridgeApi } = await import('@/lib/bridgeApi');
      const res = await bridgeApi.post<any>('patient_reset_password_otp', {
        email: forgotEmail.trim(),
        otp: forgotOtp,
        new_password: forgotNewPassword,
      });
      toast({ title: 'Password Reset!', description: res.message || 'Password updated successfully. Please log in.' });
      setShowForgotPassword(false);
      setForgotStep('email');
      setForgotEmail('');
      setForgotOtp('');
      setForgotNewPassword('');
      setForgotConfirmPassword('');
    } catch (error: any) {
      toast({ title: 'Reset Failed', description: error.message || 'Invalid or expired code. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleGuestRegister = async (e: React.FormEvent) => {
    e.preventDefault();
        if (!signupEnabled) {
      toast({ title: "Registration Disabled", description: "Patient registration is currently disabled.", variant: "destructive" });
      return;
    }

    if (!guestData.name.trim() || !guestData.mobile.trim()) {
      toast({ title: "Fields Required", description: "Name and Mobile are required", variant: "destructive" });
      return;
    }
    if (!isValidMobile(guestData.mobile)) {
      toast({ title: "Invalid Mobile Number", description: "Enter a valid 10-digit mobile number starting with 6-9", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { error, user: regUser } = await visitorRegister(guestData.name, guestData.mobile, guestData.email);
      if (error) {
        toast({ title: "Registration Failed", description: error.message, variant: "destructive" });
      } else {
        const firstName = (regUser?.name || guestData.name).split(' ')[0];
        toast({ title: "Welcome!", description: `Hi ${firstName}, you are now registered.` });
        onSuccess?.();
        finishWithNotifStep();
      }
    } catch (err: any) {
      toast({ title: "Connection Error", description: err.message || "An error occurred. Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleMobileLogin = async (e: React.FormEvent) => {
    e.preventDefault();
     if (!loginEnabled) {
      toast({ title: "Login Disabled", description: "Patient portal login is currently disabled.", variant: "destructive" });
      return;
    }

    if (!isValidMobile(mobileLoginNum)) {
      toast({ title: "Invalid Mobile Number", description: "Enter a valid 10-digit mobile number starting with 6-9", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { error, user: loginUser } = await mobileLogin(mobileLoginNum);
      if (error) {
        const isPatientAccount = error.message?.includes('password');
        toast({
          title: isPatientAccount ? "Password Required" : "Not Registered",
          description: isPatientAccount
            ? "You have a full Patient account. Please use the Email / Password login below."
            : "This mobile number is not registered. Please register first.",
          variant: "destructive"
        });
      } else {
        const firstName = (loginUser?.name || '').split(' ')[0];
        toast({ title: "Welcome back!", description: firstName ? `Hi ${firstName}, you are logged in.` : "You are now logged in." });
        finishWithNotifStep();
      }
    } catch (err: any) {
      toast({ title: "Connection Error", description: err.message || "An error occurred. Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handlePatientRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signupEnabled) {
      toast({ title: "Registration Disabled", description: "Patient registration is currently disabled.", variant: "destructive" });
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast({ title: "Password Mismatch", description: "Passwords do not match", variant: "destructive" });
      return;
    }
    if (registerData.password.length < 6) {
      toast({ title: "Weak Password", description: "At least 6 characters required", variant: "destructive" });
      return;
    }
    if (!registerData.mobile || !isValidMobile(registerData.mobile)) {
      toast({ title: "Invalid Mobile Number", description: "Enter a valid 10-digit mobile number starting with 6-9", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { error, user: regUser } = await signUp(registerData.email, registerData.password, registerData.name, registerData.mobile, 'patient');
      if (error) {
        toast({ title: "Registration Failed", description: error.message, variant: "destructive" });
      } else {
        const firstName = (regUser?.name || registerData.name).split(' ')[0];
        toast({ title: "Account Created!", description: `Welcome ${firstName}!` });
        onSuccess?.();
        finishWithNotifStep();
      }
    } catch (err: any) {
      toast({ title: "Connection Error", description: err.message || "An error occurred. Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEnabled) {
      toast({ title: "Login Disabled", description: "Patient portal login is currently disabled.", variant: "destructive" });
      return;
    }

    if (!loginData.email || !loginData.password) {
      toast({ title: "Please fill all fields", description: "Email/Mobile and Password are required", variant: "destructive" });
      return;
    }

    const id = loginData.email.trim();
    if (!isValidEmail(id) && !isValidMobile(id)) {
      toast({ title: "Invalid Email or Mobile", description: "Please enter a valid email address or 10-digit mobile number", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { error, user: loginUser } = await signIn(loginData.email, loginData.password);
      if (error) {
        toast({ title: "Login Failed", description: error.message, variant: "destructive" });
      } else {
        const firstName = (loginUser?.name || '').split(' ')[0];
        toast({ title: "Welcome Back!", description: firstName ? `Hi ${firstName}!` : "You are now logged in." });
        onSuccess?.();
        finishWithNotifStep();
      }
    } catch (err: any) {
      toast({ title: "Connection Error", description: err.message || "An error occurred. Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const optionCards = [
    {
      step: 'mobile_login' as AuthStep,
      icon: Smartphone,
      title: 'Login with Mobile',
      desc: 'Enter your registered mobile number',
      gradient: 'from-primary to-primary/70',
      iconBg: 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white',
      delay: 0,
    },
    {
      step: 'guest_register' as AuthStep,
      icon: User,
      title: 'Quick Visitor Registration',
      desc: 'One-time details, then login with mobile',
      gradient: 'from-healing-green to-healing-green/70',
      iconBg: 'bg-healing-green/10 text-healing-green group-hover:bg-healing-green group-hover:text-white',
      delay: 0.05,
    },
    {
      step: 'patient_register' as AuthStep,
      icon: Heart,
      title: 'Patient Registration',
      desc: 'Full access to health records & features',
      gradient: 'from-primary to-healing-green',
      iconBg: 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white',
      delay: 0.1,
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent 
        className="w-[calc(100vw-1rem)] sm:max-w-[440px] p-0 overflow-hidden bg-background border-0 shadow-2xl max-h-[85vh] flex flex-col rounded-3xl" 
        hideCloseButton
        aria-describedby="patient-auth-description"
      >
        <DialogTitle className="sr-only">Patient Authentication</DialogTitle>
        <DialogDescription className="sr-only" id="patient-auth-description">
          Login or register to access healthcare services
        </DialogDescription>
        
        {/* Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden px-5 py-6 text-white shrink-0"
          style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(213 69% 30%), hsl(152 62% 44%))' }}
        >
          {/* Floating particles */}
          <FloatingParticle delay={0} x="10%" y="20%" />
          <FloatingParticle delay={0.5} x="80%" y="30%" />
          <FloatingParticle delay={1} x="50%" y="60%" />
          <FloatingParticle delay={1.5} x="25%" y="70%" />
          <FloatingParticle delay={2} x="70%" y="15%" />

          {/* Close button */}
          <motion.button
            onClick={resetAndClose}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm transition-colors z-10"
          >
            <X className="w-4 h-4" />
          </motion.button>

          <div className="flex items-center gap-4">
            <motion.div
              className="relative p-3 rounded-2xl bg-white/15 backdrop-blur-sm"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <PulseRing />
              <Shield className="w-7 h-7 relative z-10" />
            </motion.div>
            <div>
              <motion.h2
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl font-bold flex items-center gap-2"
              >
                Welcome to PataKare
                <motion.span
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                >
                  <Sparkles className="w-4 h-4 text-yellow-300" />
                </motion.span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="text-white/70 text-sm flex items-center gap-1.5"
              >
                <Activity className="w-3.5 h-3.5" />
                Your secure health companion
              </motion.p>
            </div>
          </div>

          {/* Bottom wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 440 20" className="w-full h-3" preserveAspectRatio="none">
              <path d="M0,20 Q110,0 220,10 Q330,20 440,5 L440,20 Z" fill="hsl(var(--background))" />
            </svg>
          </div>
        </motion.div>

        {/* Content */}
        <div className="px-5 pb-5 pt-2 overflow-y-auto flex-1 min-w-0">
          {showNotifStep ? (
            <motion.div
              key="notif-step"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-10"
            >
              <motion.div
                className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 relative"
                animate={{ rotate: [0, -18, 15, -12, 9, -5, 0] }}
                transition={{ duration: 0.9, repeat: Infinity, repeatDelay: 0.6, ease: 'easeInOut' }}
              >
                <PulseRing />
                <Bell className="w-8 h-8 text-primary relative z-10" />
              </motion.div>
              <h3 className="font-bold text-foreground text-base">You're in!</h3>
              <p className="text-xs text-muted-foreground mt-1.5 max-w-[280px] mx-auto">
                Turning on notifications so you know the moment a provider replies —
                allow it if your browser asks.
              </p>
            </motion.div>
          ) : (
          <AnimatePresence mode="wait">
            {/* Step: Choose */}
            {step === 'choose' && (
              <motion.div
                key="choose"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-3"
              >
                {optionCards.map((card) => (
                  <motion.button
                    key={card.step}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: card.delay, duration: 0.4 }}
                    whileHover={{ scale: 1.02, x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { setStep(card.step); setShowPassword(false); }}
                    className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition-all text-left group relative overflow-hidden"
                  >
                    {/* Hover shine effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full"
                      whileHover={{ translateX: '100%' }}
                      transition={{ duration: 0.6 }}
                    />
                    <motion.div
                      className={`p-2.5 rounded-xl ${card.iconBg} transition-all duration-300 shrink-0 relative z-10`}
                      whileHover={{ rotate: 5 }}
                    >
                      <card.icon className="w-5 h-5" />
                    </motion.div>
                    <div className="flex-1 min-w-0 relative z-10">
                      <p className="font-semibold text-sm text-foreground">{card.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{card.desc}</p>
                    </div>
                    <motion.div
                      className="shrink-0 relative z-10"
                      animate={{ x: [0, 3, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity, delay: card.delay + 0.5 }}
                    >
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                    </motion.div>
                  </motion.button>
                ))}

                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-background px-3 text-muted-foreground">Already a patient?</span>
                  </div>
                </div>

                {/* Email Login */}
                <motion.form
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  onSubmit={handleEmailLogin}
                  className="space-y-3"
                >
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Email or Mobile</Label>
                    <div className="relative group">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                      <Input type="text" placeholder="Email or Mobile" className="pl-10 h-11 rounded-xl border-border/50 focus:border-primary/50 transition-all" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} required autoComplete="username" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Password</Label>
                    <div className="relative group">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                      <Input type={showPassword ? 'text' : 'password'} placeholder="Password" className="pl-10 pr-10 h-11 rounded-xl border-border/50 focus:border-primary/50 transition-all" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required autoComplete="current-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                    <Button type="submit" className="w-full h-11 rounded-xl font-semibold text-sm" disabled={loading}>
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                      Sign In
                    </Button>
                  </motion.div>
                  <div className="flex items-center justify-between text-[11px]">
                    <button type="button" onClick={() => setShowForgotPassword(true)} className="text-primary hover:underline">
                      Forgot Password?
                    </button>
                  </div>
                </motion.form>

                {/* Forgot Password Form */}
                {showForgotPassword && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-3 p-3 rounded-xl border border-border bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-foreground">
                        {forgotStep === 'email' ? 'Reset Password' : 'Enter Verification Code'}
                      </p>
                      <button
                        onClick={() => {
                          if (forgotStep === 'otp') {
                            setForgotStep('email');
                          } else {
                            setShowForgotPassword(false);
                            setForgotEmail('');
                          }
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        {forgotStep === 'otp' ? '← Back' : '✕'}
                      </button>
                    </div>

                    {forgotStep === 'email' ? (
                      <form onSubmit={handleForgotPasswordRequest} className="flex gap-2">
                        <Input type="email" placeholder="your@email.com" className="h-9 text-sm" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required autoComplete="email" />
                        <Button type="submit" size="sm" disabled={loading} className="shrink-0">
                          {loading ? '...' : 'Send'}
                        </Button>
                      </form>
                    ) : (
                      <form onSubmit={handleForgotPasswordReset} className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Code sent to <span className="font-medium text-foreground">{forgotEmail}</span> (valid 10 min)
                        </p>
                        <div className="flex justify-center py-1">
                          <InputOTP maxLength={6} value={forgotOtp} onChange={setForgotOtp}>
                            <InputOTPGroup>
                              {[0, 1, 2, 3, 4, 5].map((i) => (
                                <InputOTPSlot key={i} index={i} />
                              ))}
                            </InputOTPGroup>
                          </InputOTP>
                        </div>
                        <Input type="password" placeholder="New password (min 6 characters)" className="h-9 text-sm" value={forgotNewPassword} onChange={(e) => setForgotNewPassword(e.target.value)} autoComplete="new-password" />
                        <Input type="password" placeholder="Confirm new password" className="h-9 text-sm" value={forgotConfirmPassword} onChange={(e) => setForgotConfirmPassword(e.target.value)} autoComplete="new-password" />
                        <Button type="submit" size="sm" disabled={loading} className="w-full">
                          {loading ? '...' : 'Reset Password'}
                        </Button>
                        <button type="button" onClick={handleForgotPasswordRequest} disabled={loading} className="w-full text-center text-[11px] text-primary hover:underline">
                          Didn't get the code? Resend
                        </button>
                      </form>
                    )}
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Step: Visitor Registration */}
            {step === 'guest_register' && (
              <motion.div key="guest" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ type: 'spring', damping: 25 }}>
                <BackButton onClick={() => { setStep('choose'); setShowPassword(false); }} />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-3 rounded-xl bg-healing-green/10 border border-healing-green/20 mb-4"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-healing-green shrink-0" />
                    <span className="text-sm font-medium text-foreground">Quick Visitor Registration</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">One-time details → then login with mobile only</p>
                </motion.div>
                <form onSubmit={handleGuestRegister} className="space-y-3">
                  <AnimatedField label="Full Name *" icon={User} id="g-name" placeholder="Enter your full name" value={guestData.name} onChange={(v) => setGuestData({ ...guestData, name: v })} delay={0} required />
                  <AnimatedField label="Mobile Number *" icon={Phone} id="g-mobile" type="tel" placeholder="10-digit mobile number" maxLength={10} value={guestData.mobile} onChange={(v) => setGuestData({ ...guestData, mobile: v.replace(/\D/g, '') })} delay={0.05} required />
                  <AnimatedField label="Email (optional)" icon={Mail} id="g-email" type="email" placeholder="Email address" value={guestData.email} onChange={(v) => setGuestData({ ...guestData, email: v })} delay={0.1} />
                  <SubmitButton loading={loading} icon={ArrowRight} text="Register & Continue" />
                  <p className="text-[11px] text-center text-muted-foreground">
                    Want full access?{' '}
                    <button type="button" onClick={() => { setStep('patient_register'); setShowPassword(false); }} className="text-primary hover:underline font-medium">Register as Patient</button>
                  </p>
                </form>
              </motion.div>
            )}

            {/* Step: Mobile Login */}
            {step === 'mobile_login' && (
              <motion.div key="mobile" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ type: 'spring', damping: 25 }}>
                <BackButton onClick={() => { setStep('choose'); setShowPassword(false); }} />
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', damping: 15 }}
                  className="text-center mb-5"
                >
                  <motion.div
                    className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-3 relative"
                    animate={{ rotate: [0, 3, -3, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <PulseRing />
                    <Smartphone className="w-6 h-6 sm:w-8 sm:h-8 text-primary relative z-10" />
                  </motion.div>
                  <h3 className="font-bold text-foreground text-lg">Login with Mobile</h3>
                  <p className="text-xs text-muted-foreground mt-1">Enter your registered mobile number</p>
                </motion.div>
                <form onSubmit={handleMobileLogin} className="space-y-4">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="relative group"
                  >
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input type="tel" placeholder="Enter 10-digit mobile" className="pl-10 h-12 sm:h-14 text-base sm:text-lg text-center tracking-[0.2em] sm:tracking-[0.3em] rounded-xl border-border/50 focus:border-primary/50 font-mono" maxLength={10} value={mobileLoginNum} onChange={(e) => setMobileLoginNum(e.target.value.replace(/\D/g, ''))} required autoFocus />
                  </motion.div>
                  <SubmitButton loading={loading} icon={LogIn} text="Login" disabled={mobileLoginNum.length < 10} />
                  <p className="text-[11px] text-center text-muted-foreground">
                    New here?{' '}
                    <button type="button" onClick={() => { setStep('guest_register'); setShowPassword(false); }} className="text-primary hover:underline font-medium">Register first</button>
                  </p>
                </form>
              </motion.div>
            )}

            {/* Step: Full Patient Registration */}
            {step === 'patient_register' && (
              <motion.div key="patient" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ type: 'spring', damping: 25 }}>
                <BackButton onClick={() => { setStep('choose'); setShowPassword(false); }} />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 mb-4"
                >
                  <div className="flex items-center gap-2">
                    <Heart className="w-5 h-5 text-destructive shrink-0" />
                    <span className="text-sm font-medium text-foreground">Patient Registration</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Full access to health records & all features</p>
                </motion.div>
                <form onSubmit={handlePatientRegister} className="space-y-3">
                  <AnimatedField label="Full Name *" icon={User} placeholder="Enter your full name" value={registerData.name} onChange={(v) => setRegisterData({ ...registerData, name: v })} delay={0} required />
                  <AnimatedField label="Mobile *" icon={Phone} type="tel" placeholder="10-digit mobile number" maxLength={10} value={registerData.mobile} onChange={(v) => setRegisterData({ ...registerData, mobile: v.replace(/\D/g, '') })} delay={0.03} required />
                  <AnimatedField label="Email *" icon={Mail} type="email" placeholder="Email address" value={registerData.email} onChange={(v) => setRegisterData({ ...registerData, email: v })} delay={0.06} required autoComplete="email" />
                  <AnimatedField label="Password *" icon={Lock} type={showPassword ? 'text' : 'password'} placeholder="Create password (min 6 chars)" value={registerData.password} onChange={(v) => setRegisterData({ ...registerData, password: v })} delay={0.09} required autoComplete="new-password"
                    rightElement={
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    }
                  />
                  <AnimatedField label="Confirm Password *" icon={Lock} type={showPassword ? 'text' : 'password'} placeholder="Confirm password" value={registerData.confirmPassword} onChange={(v) => setRegisterData({ ...registerData, confirmPassword: v })} delay={0.12} required autoComplete="new-password" />
                  <SubmitButton loading={loading} icon={UserPlus} text="Create Patient Account" />
                </form>
              </motion.div>
            )}
          </AnimatePresence>
          )}

          {/* Provider note */}
          {!showNotifStep && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-5 p-3 rounded-xl bg-muted/50 border border-border/50"
          >
            <p className="text-[11px] text-center text-muted-foreground">
              <strong>Healthcare Providers:</strong> Doctors, Labs, and Pharmacies should use the{' '}
              <Link to="/auth" className="text-primary hover:underline">Member Login</Link>.
            </p>
          </motion.div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

/* ─── Sub-components ─── */

const BackButton = ({ onClick }: { onClick: () => void }) => (
  <motion.button
    initial={{ opacity: 0, x: -5 }}
    animate={{ opacity: 1, x: 0 }}
    onClick={onClick}
    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 group"
  >
    <motion.span whileHover={{ x: -3 }}><ArrowLeft className="w-4 h-4" /></motion.span>
    Back
  </motion.button>
);

const AnimatedField = ({ label, icon: Icon, delay = 0, value, onChange, rightElement, ...inputProps }: {
  label: string;
  icon: React.ElementType;
  delay?: number;
  value: string;
  onChange: (v: string) => void;
  rightElement?: React.ReactNode;
  [key: string]: any;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.3 }}
    className="space-y-1.5"
  >
    <Label className="text-sm font-medium">{label}</Label>
    <div className="relative group">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
      <Input
        className={`pl-10 h-11 rounded-xl border-border/50 focus:border-primary/50 transition-all ${rightElement ? 'pr-10' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...inputProps}
      />
      {rightElement}
    </div>
  </motion.div>
);

const SubmitButton = ({ loading, icon: Icon, text, disabled }: {
  loading: boolean;
  icon: React.ElementType;
  text: string;
  disabled?: boolean;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.15 }}
    whileHover={{ scale: 1.01 }}
    whileTap={{ scale: 0.99 }}
  >
    <Button type="submit" className="w-full h-11 rounded-xl font-semibold text-sm" disabled={loading || disabled}>
      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Icon className="w-4 h-4 mr-2" />}
      {text}
    </Button>
  </motion.div>
);

PatientAuthModal.displayName = 'PatientAuthModal';

export default PatientAuthModal;
